import { useTranslation } from "react-i18next";
import { Badge } from "catalyst-ui-kit/badge";
import { formatPrettyDate } from "utils/helperFunctions";
import type { InvoiceSendStatus } from "types/Invoice";

// How each send status looks: the server's status picks the badge colour and
// label. "Sent" means the mail provider accepted the message, not that it was
// delivered, so the labels never say "delivered".
const BADGE_BY_STATUS = {
  sent: { color: "green", labelKey: "invoice_send_status_sent", label: "Sent" },
  not_sent: {
    color: "zinc",
    labelKey: "invoice_send_status_not_sent",
    label: "Not sent",
  },
  failed: {
    color: "red",
    labelKey: "invoice_send_status_failed",
    label: "Sending failed",
  },
} as const;

/** "2026-10-13 · Rasa K. · billing@acme.lt" — when, who, and to which address. */
function sentDetails(send: InvoiceSendStatus): string {
  return [
    send.lastSentAt ? formatPrettyDate(send.lastSentAt) : "",
    send.lastSentBy?.name,
    send.lastSentTo,
  ]
    .filter(Boolean)
    .join(" · ");
}

type Props = {
  /** Absent for invoices that cannot be emailed from here (e.g. Finvalda ones). */
  send?: InvoiceSendStatus;
  /** Adds the "date · name · to" line under a sent badge. */
  showDetails?: boolean;
};

export default function SendStatusBadge({ send, showDetails = false }: Props) {
  const { t } = useTranslation();
  if (!send) return null;

  const badge = BADGE_BY_STATUS[send.status];
  const details = showDetails && send.status === "sent" ? sentDetails(send) : "";

  return (
    <div className="flex flex-col items-start gap-0.5">
      {/* The provider's error is the reason behind a failed (or later failed) attempt. */}
      <Badge color={badge.color} title={send.lastError || undefined}>
        {t(badge.labelKey, { defaultValue: badge.label })}
      </Badge>
      {details && <span className="text-xs text-zinc-500">{details}</span>}
    </div>
  );
}
